import { useState } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { LayoutDashboard, CheckCircle2, ArrowRight, Activity, Users, Globe2, ShieldCheck } from 'lucide-react';

export default function Register() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        telegram: '',
        trafficSource: '',
        estimatedVolume: '',
        geos: ''
    });

    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const submitFunc = httpsCallable(functions, 'submitAffiliateApplication');
            await submitFunc(formData);

            setSubmitted(true);
        } catch (err: any) {
            console.error('Error submitting application:', err);
            setError(err.message || 'An error occurred while submitting your application. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    if (submitted) {
        return (
            <div className="min-h-screen bg-[#070B14] flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-600/20 rounded-full blur-[120px] mix-blend-screen animate-pulse-slow"></div>
                    <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-600/20 rounded-full blur-[120px] mix-blend-screen animate-pulse-slow object-delay-1000"></div>
                </div>

                <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10 text-center">
                    <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-green-500/30">
                        <CheckCircle2 className="w-10 h-10 text-green-400" />
                    </div>
                    <h2 className="text-3xl font-extrabold text-white glow-text mb-2">Application Received!</h2>
                    <p className="text-slate-400 text-lg mb-8">
                        Thank you for applying to the Creative Affiliate program.
                        Our team will review your application and get back to you
                        via email or Telegram within 24-48 hours.
                    </p>
                    <Link to="/login" className="btn-primary inline-flex items-center">
                        Return to Login <ArrowRight className="ml-2" size={16} />
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#070B14] flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute inset-0 z-0 pointer-events-none">
                <div className="absolute top-1/4 -left-1/4 w-[800px] h-[800px] bg-primary-600/10 rounded-full blur-[120px] mix-blend-screen animate-pulse-slow"></div>
                <div className="absolute -bottom-1/4 -right-1/4 w-[800px] h-[800px] bg-accent-600/10 rounded-full blur-[120px] mix-blend-screen animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
            </div>

            <div className="sm:mx-auto sm:w-full sm:max-w-[1000px] relative z-10">
                <div className="text-center mb-10">
                    <div className="flex justify-center mb-6">
                        <div className="flex w-16 h-16 rounded-2xl bg-linear-to-br from-primary-500 to-accent-600 p-[2px] shadow-2xl shadow-primary-500/20">
                            <div className="w-full h-full bg-[#070B14] rounded-2xl flex items-center justify-center">
                                <LayoutDashboard className="w-8 h-8 text-white" />
                            </div>
                        </div>
                    </div>
                    <h2 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight glow-text mb-4">
                        Join Our Elite <br className="hidden sm:block" /> Affiliate Program
                    </h2>
                    <p className="mt-2 text-xl text-slate-400 max-w-2xl mx-auto">
                        Apply now to access high-converting offers, exclusive creatives, and industry-leading payouts.
                    </p>
                </div>

                <div className="grid md:grid-cols-5 gap-8 items-start">
                    {/* Value Props Sidebar */}
                    <div className="md:col-span-2 space-y-6 hidden md:block">
                        <div className="glass-dark p-6 rounded-2xl border border-white/5 bg-white/[0.02]">
                            <h3 className="text-xl font-bold text-white mb-6">Why Partner With Us?</h3>

                            <div className="space-y-6">
                                <div className="flex gap-4">
                                    <div className="shrink-0 w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                                        <Activity className="w-5 h-5 text-primary-400" />
                                    </div>
                                    <div>
                                        <h4 className="text-white font-semibold flex items-center">High Conversion Rates</h4>
                                        <p className="text-sm text-slate-400 mt-1">Our funnels are aggressively optimized to maximize your EPCs.</p>
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <div className="shrink-0 w-10 h-10 rounded-full bg-accent-500/20 flex items-center justify-center">
                                        <Globe2 className="w-5 h-5 text-accent-400" />
                                    </div>
                                    <div>
                                        <h4 className="text-white font-semibold flex items-center">Global Coverage</h4>
                                        <p className="text-sm text-slate-400 mt-1">Monetize traffic from dozens of Tier 1 and Tier 2 countries.</p>
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <div className="shrink-0 w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                                        <ShieldCheck className="w-5 h-5 text-emerald-400" />
                                    </div>
                                    <div>
                                        <h4 className="text-white font-semibold flex items-center">Reliable Payouts</h4>
                                        <p className="text-sm text-slate-400 mt-1">Get paid on time, every time, with flexible payment methods.</p>
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <div className="shrink-0 w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                                        <Users className="w-5 h-5 text-blue-400" />
                                    </div>
                                    <div>
                                        <h4 className="text-white font-semibold flex items-center">Dedicated Support</h4>
                                        <p className="text-sm text-slate-400 mt-1">Direct access to your affiliate manager via Telegram.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Application Form */}
                    <div className="md:col-span-3">
                        <div className="glass-dark py-8 px-4 sm:px-10 rounded-3xl shadow-2xl shadow-black/50 border border-white/10 relative backdrop-blur-xl">
                            <div className="absolute inset-0 rounded-3xl bg-linear-to-b from-white/5 to-transparent pointer-events-none"></div>

                            <form className="space-y-6 relative" onSubmit={handleSubmit}>
                                {error && (
                                    <div className="bg-red-500/10 border border-red-500/50 text-red-500 p-4 rounded-xl text-sm font-medium animate-shake">
                                        {error}
                                    </div>
                                )}

                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                    <div className="sm:col-span-2">
                                        <label htmlFor="name" className="block text-sm font-medium text-slate-300">
                                            Full Name / Company Name <span className="text-red-400">*</span>
                                        </label>
                                        <div className="mt-2 text-white">
                                            <input
                                                id="name"
                                                name="name"
                                                type="text"
                                                required
                                                value={formData.name}
                                                onChange={handleChange}
                                                className="input-field w-full text-white"
                                                placeholder="John Doe or Acme Inc."
                                                style={{ color: 'white' }}
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="email" className="block text-sm font-medium text-slate-300">
                                            Email Address <span className="text-red-400">*</span>
                                        </label>
                                        <div className="mt-2 text-white">
                                            <input
                                                id="email"
                                                name="email"
                                                type="email"
                                                required
                                                value={formData.email}
                                                onChange={handleChange}
                                                className="input-field w-full text-white"
                                                placeholder="you@example.com"
                                                style={{ color: 'white' }}
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="telegram" className="block text-sm font-medium text-slate-300">
                                            Telegram Username <span className="text-red-400">*</span>
                                        </label>
                                        <div className="mt-2 relative text-white">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <span className="text-slate-500">@</span>
                                            </div>
                                            <input
                                                id="telegram"
                                                name="telegram"
                                                type="text"
                                                required
                                                value={formData.telegram}
                                                onChange={handleChange}
                                                className="input-field w-full pl-8 text-white"
                                                placeholder="username"
                                                style={{ color: 'white' }}
                                            />
                                        </div>
                                    </div>

                                    <div className="sm:col-span-2">
                                        <label htmlFor="trafficSource" className="block text-sm font-medium text-slate-300">
                                            Primary Traffic Sources <span className="text-red-400">*</span>
                                        </label>
                                        <div className="mt-2">
                                            <select
                                                id="trafficSource"
                                                name="trafficSource"
                                                required
                                                value={formData.trafficSource}
                                                onChange={handleChange}
                                                className="input-field w-full text-white bg-slate-900 border-white/10"
                                            >
                                                <option value="" disabled>Select your main traffic source...</option>
                                                <option value="Facebook Ads">Facebook / Meta Ads</option>
                                                <option value="TikTok Ads">TikTok Ads</option>
                                                <option value="Google Ads">Google Ads</option>
                                                <option value="Native Ads">Native Ads (Taboola, Outbrain, etc.)</option>
                                                <option value="Email Marketing">Email Marketing</option>
                                                <option value="SEO">SEO / Organic Search</option>
                                                <option value="Influencer">Influencer / Social Media</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="estimatedVolume" className="block text-sm font-medium text-slate-300">
                                            Estimated Monthly Volume (Sales)
                                        </label>
                                        <div className="mt-2">
                                            <select
                                                id="estimatedVolume"
                                                name="estimatedVolume"
                                                value={formData.estimatedVolume}
                                                onChange={handleChange}
                                                className="input-field w-full text-white bg-slate-900 border-white/10"
                                            >
                                                <option value="" disabled>Select volume...</option>
                                                <option value="0-50">0 - 50 sales</option>
                                                <option value="51-200">51 - 200 sales</option>
                                                <option value="201-500">201 - 500 sales</option>
                                                <option value="500+">500+ sales</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="geos" className="block text-sm font-medium text-slate-300">
                                            Top Target GEOs
                                        </label>
                                        <div className="mt-2 text-white">
                                            <input
                                                id="geos"
                                                name="geos"
                                                type="text"
                                                value={formData.geos}
                                                onChange={handleChange}
                                                className="input-field w-full text-white"
                                                placeholder="e.g. US, UK, DE, FR"
                                                style={{ color: 'white' }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-4 flex w-full">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full h-12 flex justify-center items-center py-3.5 px-4 border border-transparent rounded-xl shadow-lg shadow-primary-500/25 text-base font-bold text-white bg-linear-to-r from-primary-500 to-accent-600 hover:from-primary-400 hover:to-accent-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 focus:ring-offset-[#070B14] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 cursor-pointer"
                                    >
                                        {loading ? (
                                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        ) : (
                                            <span className="flex items-center">Submit Application <ArrowRight className="ml-2 w-5 h-5" /></span>
                                        )}
                                    </button>
                                </div>

                                <div className="text-center mt-6">
                                    <p className="text-sm text-slate-400">
                                        Already have an account?{' '}
                                        <Link to="/login" className="font-semibold text-primary-400 hover:text-primary-300 transition-colors">
                                            Log in here
                                        </Link>
                                    </p>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
